<template>
  <div id="Center" class="flex flex-1 flex-col grow h-full"
  >
    <!-- Center not found state: centerId doesn't match any user center -->
    <div v-if="loaded && !center"
      class="flex flex-col grow relative overflow-hidden">

      <!-- Background ghost cards — decorative, behind everything -->
      <div class="absolute inset-0 pointer-events-none ghost-layer">
        <div class="grid grid-cols-2 gap-3 px-4 pt-2">
          <div class="ghost-card rounded-xl p-4">
            <div class="ghost-line w-14 h-1.5 rounded-full mb-3"></div>
            <div class="ghost-line-accent w-20 h-2.5 rounded-full mb-3"></div>
            <div class="ghost-line w-10 h-1 rounded-full"></div>
          </div>
          <div class="ghost-card rounded-xl p-4">
            <div class="ghost-line w-12 h-1.5 rounded-full mb-3"></div>
            <div class="flex items-end gap-1 h-10 mt-1">
              <div class="flex-1 rounded-t ghost-bar" style="height: 50%;"></div>
              <div class="flex-1 rounded-t ghost-bar-accent" style="height: 80%;"></div>
              <div class="flex-1 rounded-t ghost-bar" style="height: 35%;"></div>
              <div class="flex-1 rounded-t ghost-bar-accent" style="height: 65%;"></div>
              <div class="flex-1 rounded-t ghost-bar" style="height: 45%;"></div>
            </div>
          </div>
        </div>
        <div class="px-4 mt-3">
          <div class="ghost-card rounded-xl p-4">
            <div class="flex items-center gap-3">
              <div class="ghost-line-accent w-8 h-8 rounded-lg shrink-0"></div>
              <div class="flex-1">
                <div class="ghost-line w-24 h-1.5 rounded-full mb-2"></div>
                <div class="ghost-line w-16 h-1 rounded-full"></div>
              </div>
              <div class="ghost-line w-14 h-2 rounded-full"></div>
            </div>
          </div>
        </div>
      </div>

      <!-- Centered not-found content -->
      <div class="flex flex-col items-center justify-center text-center grow relative z-10 px-8">
        <!-- Icon — calm, neutral presence -->
        <div class="relative mb-5">
          <div class="w-14 h-14 rounded-full flex items-center justify-center"
            style="background: linear-gradient(145deg, rgba(148, 163, 184, 0.12), rgba(148, 163, 184, 0.04)); border: 1.5px solid rgba(148, 163, 184, 0.14);">
            <div class="w-10 h-10 rounded-full flex items-center justify-center bg-card"
              style="box-shadow: 0 3px 12px rgba(0, 0, 0, 0.06);">
              <MagnifyingGlassIcon class="h-5 w-5 text-muted-foreground" />
            </div>
          </div>
        </div>

        <!-- Title & description -->
        <h1 class="font-bold text-center text-[20px] tracking-tight leading-tight text-foreground">
          {{ $t('centernotfound.title') }}
        </h1>
        <p class="text-center text-[13px] leading-relaxed mt-1.5 max-w-[240px] text-muted-foreground">
          {{ $t('centernotfound.description') }}
        </p>

        <!-- CTA button — standard cyan, helpful action -->
        <div class="w-full max-w-[260px] mt-6">
          <button @click="goToDashboard"
            class="center-cta flex items-center justify-center gap-2 w-full h-[46px] text-white font-bold text-[14px] rounded-2xl active:scale-[0.97] transition-all duration-150">
            <ArrowLeftIcon class="h-4.5 w-4.5 stroke-[2.5]" />
            {{ $t('centernotfound.back_to_dashboard') }}
          </button>
        </div>
      </div>
    </div>

    <!-- Disconnected state: center credentials expired -->
    <div v-else-if="loaded && isDisconnected"
      class="flex flex-col grow relative overflow-hidden">

      <!-- Background ghost cards — decorative, behind everything -->
      <div class="absolute inset-0 pointer-events-none ghost-layer">
        <div class="grid grid-cols-2 gap-3 px-4 pt-2">
          <div class="ghost-card rounded-xl p-4">
            <div class="ghost-line w-14 h-1.5 rounded-full mb-3"></div>
            <div class="ghost-line-accent w-20 h-2.5 rounded-full mb-3"></div>
            <div class="ghost-line w-10 h-1 rounded-full"></div>
          </div>
          <div class="ghost-card rounded-xl p-4">
            <div class="ghost-line w-12 h-1.5 rounded-full mb-3"></div>
            <div class="flex items-end gap-1 h-10 mt-1">
              <div class="flex-1 rounded-t ghost-bar" style="height: 50%;"></div>
              <div class="flex-1 rounded-t ghost-bar-accent" style="height: 80%;"></div>
              <div class="flex-1 rounded-t ghost-bar" style="height: 35%;"></div>
              <div class="flex-1 rounded-t ghost-bar-accent" style="height: 65%;"></div>
              <div class="flex-1 rounded-t ghost-bar" style="height: 45%;"></div>
            </div>
          </div>
        </div>
        <div class="px-4 mt-3">
          <div class="ghost-card rounded-xl p-4">
            <div class="flex items-center gap-3">
              <div class="ghost-line-accent w-8 h-8 rounded-lg shrink-0"></div>
              <div class="flex-1">
                <div class="ghost-line w-24 h-1.5 rounded-full mb-2"></div>
                <div class="ghost-line w-16 h-1 rounded-full"></div>
              </div>
              <div class="ghost-line w-14 h-2 rounded-full"></div>
            </div>
          </div>
        </div>
      </div>

      <!-- Centered disconnected content -->
      <div class="flex flex-col items-center justify-center text-center grow relative z-10 px-8">
        <!-- Icon with warning glow -->
        <div class="relative mb-5">
          <div class="absolute -inset-3 rounded-full animate-pulse-slow"
            style="background: radial-gradient(circle, rgba(245, 158, 11, 0.10) 0%, transparent 65%);">
          </div>
          <div class="w-14 h-14 rounded-full flex items-center justify-center"
            style="background: linear-gradient(145deg, rgba(245, 158, 11, 0.13), rgba(245, 158, 11, 0.04)); border: 1.5px solid rgba(245, 158, 11, 0.16);">
            <div class="w-10 h-10 rounded-full flex items-center justify-center bg-card"
              style="box-shadow: 0 3px 12px rgba(245, 158, 11, 0.14);">
              <ExclamationTriangleIcon class="h-5 w-5 text-amber-500" />
            </div>
          </div>
        </div>

        <!-- Title & description -->
        <h1 class="font-bold text-center text-[20px] tracking-tight leading-tight text-foreground">
          {{ $t('centerdisconnected.title') }}
        </h1>
        <p class="text-center text-[13px] leading-relaxed mt-1.5 max-w-[240px] text-muted-foreground">
          {{ $t('centerdisconnected.description') }}
        </p>

        <!-- CTA button -->
        <div class="w-full max-w-[260px] mt-6">
          <button @click="reconnect"
            class="center-cta flex items-center justify-center gap-2 w-full h-[46px] text-white font-bold text-[14px] rounded-2xl active:scale-[0.97] transition-all duration-150">
            <ArrowPathIcon class="h-4.5 w-4.5 stroke-[2.5]" />
            {{ $t('centerdisconnected.reconnect') }}
          </button>
        </div>
      </div>
    </div>

    <!-- Server error state: center data API failed (e.g. 500) -->
    <div v-else-if="loaded && isServerError"
      class="flex flex-col grow relative overflow-hidden">

      <!-- Background ghost cards — decorative -->
      <div class="absolute inset-0 pointer-events-none ghost-layer">
        <div class="grid grid-cols-2 gap-3 px-4 pt-2">
          <div class="ghost-card rounded-xl p-4">
            <div class="ghost-line w-14 h-1.5 rounded-full mb-3"></div>
            <div class="ghost-line-accent w-20 h-2.5 rounded-full mb-3"></div>
            <div class="ghost-line w-10 h-1 rounded-full"></div>
          </div>
          <div class="ghost-card rounded-xl p-4">
            <div class="ghost-line w-12 h-1.5 rounded-full mb-3"></div>
            <div class="flex items-end gap-1 h-10 mt-1">
              <div class="flex-1 rounded-t ghost-bar" style="height: 50%;"></div>
              <div class="flex-1 rounded-t ghost-bar-accent" style="height: 80%;"></div>
              <div class="flex-1 rounded-t ghost-bar" style="height: 35%;"></div>
              <div class="flex-1 rounded-t ghost-bar-accent" style="height: 65%;"></div>
              <div class="flex-1 rounded-t ghost-bar" style="height: 45%;"></div>
            </div>
          </div>
        </div>
        <div class="px-4 mt-3">
          <div class="ghost-card rounded-xl p-4">
            <div class="flex items-center gap-3">
              <div class="ghost-line-accent w-8 h-8 rounded-lg shrink-0"></div>
              <div class="flex-1">
                <div class="ghost-line w-24 h-1.5 rounded-full mb-2"></div>
                <div class="ghost-line w-16 h-1 rounded-full"></div>
              </div>
              <div class="ghost-line w-14 h-2 rounded-full"></div>
            </div>
          </div>
        </div>
      </div>

      <!-- Centered server error content -->
      <div class="flex flex-col items-center justify-center text-center grow relative z-10 px-8">
        <div class="relative mb-5">
          <div class="w-14 h-14 rounded-full flex items-center justify-center"
            style="background: linear-gradient(145deg, rgba(245, 158, 11, 0.13), rgba(245, 158, 11, 0.04)); border: 1.5px solid rgba(245, 158, 11, 0.16);">
            <div class="w-10 h-10 rounded-full flex items-center justify-center bg-card"
              style="box-shadow: 0 3px 12px rgba(245, 158, 11, 0.14);">
              <ExclamationTriangleIcon class="h-5 w-5 text-amber-500" />
            </div>
          </div>
        </div>

        <h1 class="font-bold text-center text-[20px] tracking-tight leading-tight text-foreground">
          {{ $t('centerservererror.title') }}
        </h1>
        <p class="text-center text-[13px] leading-relaxed mt-1.5 max-w-[240px] text-muted-foreground">
          {{ $t('centerservererror.description') }}
        </p>
      </div>
    </div>

    <!-- Populated state: cards exist -->
    <div v-else-if="loaded && salesReportsStore.salesReports && salesReportsStore.salesReports.length > 0">
      <!-- Desktop: single focused report shell -->
      <template v-if="isDesktop">
        <ReportDesktopShell
          :center-id="centerId"
          :is-active="isCenterActive"
        />
      </template>

      <!-- Mobile: compact sales cards grid -->
      <template v-else>
        <TransitionGroup name="fade" tag="div"
          class="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 2xl:grid-cols-4 gap-5">
          <div v-for="sr in salesReportsStore.salesReports" :key="sr.id"
            @click="goToCenterCard(sr.id)">
            <SalesCard :center="centerId" :component="sr.id" />
          </div>
        </TransitionGroup>

        <AddCardButton @click="showCreateSheet = true" class="mt-5" />
      </template>
    </div>

    <!-- Empty state: no cards configured -->
    <div
      v-else-if="loaded && (!salesReportsStore.salesReports || salesReportsStore.salesReports.length === 0)"
      class="flex flex-col grow relative overflow-hidden">

      <!-- Background ghost cards — decorative, behind everything -->
      <div class="absolute inset-0 pointer-events-none ghost-layer">
        <div class="grid grid-cols-2 gap-3 px-4 pt-2">
          <div class="ghost-card rounded-xl p-4">
            <div class="ghost-line w-14 h-1.5 rounded-full mb-3"></div>
            <div class="ghost-line-accent w-20 h-2.5 rounded-full mb-3"></div>
            <div class="ghost-line w-10 h-1 rounded-full"></div>
          </div>
          <div class="ghost-card rounded-xl p-4">
            <div class="ghost-line w-12 h-1.5 rounded-full mb-3"></div>
            <div class="flex items-end gap-1 h-10 mt-1">
              <div class="flex-1 rounded-t ghost-bar" style="height: 50%;"></div>
              <div class="flex-1 rounded-t ghost-bar-accent" style="height: 80%;"></div>
              <div class="flex-1 rounded-t ghost-bar" style="height: 35%;"></div>
              <div class="flex-1 rounded-t ghost-bar-accent" style="height: 65%;"></div>
              <div class="flex-1 rounded-t ghost-bar" style="height: 45%;"></div>
            </div>
          </div>
        </div>
        <div class="px-4 mt-3">
          <div class="ghost-card rounded-xl p-4">
            <div class="flex items-center gap-3">
              <div class="ghost-line-accent w-8 h-8 rounded-lg shrink-0"></div>
              <div class="flex-1">
                <div class="ghost-line w-24 h-1.5 rounded-full mb-2"></div>
                <div class="ghost-line w-16 h-1 rounded-full"></div>
              </div>
              <div class="ghost-line w-14 h-2 rounded-full"></div>
            </div>
          </div>
        </div>
      </div>

      <!-- Centered content — on top of ghost cards -->
      <div class="flex flex-col items-center justify-center text-center grow relative z-10 px-8">
        <!-- Icon with glow -->
        <div class="relative mb-5">
          <div class="absolute -inset-3 rounded-full animate-pulse-slow"
            style="background: radial-gradient(circle, rgba(6, 200, 255, 0.10) 0%, transparent 65%);">
          </div>
          <div class="w-14 h-14 rounded-full flex items-center justify-center"
            style="background: linear-gradient(145deg, rgba(6, 200, 255, 0.13), rgba(6, 200, 255, 0.04)); border: 1.5px solid rgba(6, 200, 255, 0.16);">
            <div class="w-10 h-10 rounded-full flex items-center justify-center bg-card"
              style="box-shadow: 0 3px 12px rgba(6, 200, 255, 0.14);">
              <PlusIcon class="h-4.5 w-4.5" style="color: #06C8FF;" />
            </div>
          </div>
        </div>

        <!-- Title & description -->
        <h1 class="font-bold text-center text-[20px] tracking-tight leading-tight text-foreground">
          {{ $t('nocomponent.title') }}
        </h1>
        <p class="text-center text-[13px] leading-relaxed mt-1.5 max-w-[240px] text-muted-foreground">
          {{ $t('nocomponent.description') }}
        </p>

        <!-- CTA button -->
        <div class="w-full max-w-[260px] mt-6">
          <button @click="showCreateSheet = true"
            class="center-cta flex items-center justify-center gap-2 w-full h-[46px] text-white font-bold text-[14px] rounded-2xl active:scale-[0.97] transition-all duration-150">
            <PlusIcon class="h-4.5 w-4.5 stroke-[2.5]" />
            {{ $t('nocomponent.new') }}
          </button>
        </div>
      </div>
    </div>

    <CreateComponentSheet v-model:isOpen="showCreateSheet" />
  </div>
</template>
<script setup lang="ts">
defineOptions({ name: 'Center' })

import { useRouter } from 'vue-router';
import SalesCard from '../components/SalesCard.vue';
import { useSalesReportsStore } from '../stores/salesReports';
import { useUserManagerStore } from '../stores/userManagerStore';
import { computed, onMounted, onActivated, onDeactivated, ref, watch } from 'vue';
import { Center } from '../user'
import { getSmartTimeAggregation } from '../types/customCharts'
import { PlusIcon, ExclamationTriangleIcon, MagnifyingGlassIcon, ArrowPathIcon, ArrowLeftIcon } from '@heroicons/vue/24/outline'
import CreateComponentSheet from '../components/bottomsheet/CreateComponentSheet.vue'
import AddCardButton from '../components/buttons/AddCardButton.vue'
import { useIsDesktop } from '../composables/useIsDesktop'
import ReportDesktopShell from '../components/desktop/ReportDesktopShell.vue'

const userManagerStore = useUserManagerStore();
const salesReportsStore = useSalesReportsStore();
const loaded = ref(false);

// On desktop, the sidebar's "Add" button also sets this ref
import { useDesktopReportNav } from '../composables/useDesktopReportNav'
const { showCreateSheet, resetToFirst } = useDesktopReportNav()
const router = useRouter();
const isDesktop = useIsDesktop();

// Track keep-alive activation — teleported content persists at the target
// even when the parent is deactivated, causing duplicates in #topbar-period-tabs.
const isCenterActive = ref(true)
onActivated(() => { isCenterActive.value = true })
onDeactivated(() => { isCenterActive.value = false })

const centerId = computed(() => {
  return parseInt(router.currentRoute.value.params.centerId as string);
})

const center = computed(() => {
  return userManagerStore.user?.centers.find((center: Center) => center.id === centerId.value)
})

const isDisconnected = computed(() => {
  return salesReportsStore.isCenterDisconnected(centerId.value)
})

const isServerError = computed(() => {
  return salesReportsStore.allReportsServerError(centerId.value)
})

const reconnect = () => {
  router.push({ name: 'CenterSignin', query: { context: 'reconnect' } })
}

const goToDashboard = () => {
  router.push({ name: 'Dashboard' })
}

const emits = defineEmits<{
  (e: 'loaded', center: Center): void
}>()

const loadSalesReportsData = async () => {
  loaded.value = false;
  if (!center.value) {
    loaded.value = true;
    return;
  }
  await salesReportsStore.initCenterComponents()
  salesReportsStore.setActiveCenter(centerId.value);
  await salesReportsStore.fetchSalesReportsDatas();
  loaded.value = true;

  // Prefetch hub data in background for faster CenterReport display
  salesReportsStore.salesReports?.forEach(sr => {
    const smartAgg = getSmartTimeAggregation(sr.date.startDate.toJSDate(), sr.date.endDate.toJSDate())
    if (!sr.clientTypesReport) sr.fetchClientTypesReport(smartAgg).catch(() => {})
    if (!sr.clientsReport) sr.fetchClientsReport(smartAgg).catch(() => {})
    if (!sr.membersReport) sr.fetchMembersReport(['recency'], ['count'], smartAgg).catch(() => {})
  })
}

const goToCenterCard = (srId: string) => {
  router.push({ name: 'CenterReports', params: { centerId: centerId.value.toString(), id: srId } })
}

watch(centerId, async () => {
  resetToFirst()
  await loadSalesReportsData();
})

onMounted(async () => {
  if (center.value) {
    emits("loaded", center.value)
  }
  await loadSalesReportsData();
})
</script>
<style scoped>
/* Ghost layer — fades out toward the bottom */
.ghost-layer {
  mask-image: linear-gradient(to bottom, rgba(0,0,0,0.45) 0%, rgba(0,0,0,0.15) 40%, transparent 65%);
  -webkit-mask-image: linear-gradient(to bottom, rgba(0,0,0,0.45) 0%, rgba(0,0,0,0.15) 40%, transparent 65%);
}

/* Ghost cards */
.ghost-card {
  background: var(--card);
  border: 1px solid rgba(0, 0, 0, 0.04);
}

:is(.dark *).ghost-card {
  border: 1px solid rgba(255, 255, 255, 0.04);
}

.ghost-line {
  background: rgba(0, 0, 0, 0.06);
}

:is(.dark *).ghost-line {
  background: rgba(255, 255, 255, 0.06);
}

.ghost-line-accent {
  background: rgba(6, 200, 255, 0.10);
}

:is(.dark *).ghost-line-accent {
  background: rgba(6, 200, 255, 0.08);
}

.ghost-bar {
  background: rgba(0, 0, 0, 0.05);
}

:is(.dark *).ghost-bar {
  background: rgba(255, 255, 255, 0.05);
}

.ghost-bar-accent {
  background: rgba(6, 200, 255, 0.12);
}

:is(.dark *).ghost-bar-accent {
  background: rgba(6, 200, 255, 0.08);
}

/* CTA button */
.center-cta {
  background: linear-gradient(135deg, #06C8FF 0%, #009FE3 100%);
  box-shadow: 0 6px 24px rgba(6, 200, 255, 0.25), inset 0 1px 0 rgba(255, 255, 255, 0.18);
}

:is(.dark *).center-cta {
  box-shadow: 0 4px 16px rgba(6, 200, 255, 0.12), inset 0 1px 0 rgba(255, 255, 255, 0.10);
}

/* Pulse animation for empty state icon */
.animate-pulse-slow {
  animation: pulseSlow 3.5s ease-in-out infinite;
}

@keyframes pulseSlow {
  0%, 100% {
    opacity: 0.3;
    transform: scale(1);
  }
  50% {
    opacity: 1;
    transform: scale(1.12);
  }
}
</style>
